// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Badge, Drawer, Group, Loader, Pagination, Paper, ScrollArea, Stack, Table, Text, Title } from '@mantine/core';
import { APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM, formatUsd } from '@polarissleep/shared';
import { IconAlertTriangle } from '@tabler/icons-react';
import dayjs from 'dayjs';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { PatientCharge } from '../../hooks/usePatientCharges';
import { TableAnchor } from '../TableAnchor';
import sectionClasses from './dashboard-section.module.css';

const BRAND = '#640040';

const ITEMS_PER_PAGE = 10;

const scrollAreaStyles = {
  thumb: { backgroundColor: 'var(--mantine-color-polaris-5)' },
};

interface HistoryOfChargesSectionProps {
  charges: PatientCharge[];
  loading: boolean;
}

/**
 * Patient-facing billing history table. Mirrors the design's column set:
 * Invoice Number, Purpose, Insurance/Patient contributions, Balance Due,
 * Due, Payment Status, and per-row Actions (Pay Now / View Receipt).
 */
export function HistoryOfChargesSection({ charges, loading }: HistoryOfChargesSectionProps): JSX.Element {
  const [page, setPage] = useState(1);
  const [selectedCharge, setSelectedCharge] = useState<PatientCharge | null>(null);

  const totalPages = Math.max(1, Math.ceil(charges.length / ITEMS_PER_PAGE));
  const paginated = useMemo(() => charges.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE), [charges, page]);

  let body: JSX.Element;
  if (loading) {
    body = (
      <Group justify="center" py="md">
        <Loader size="sm" />
      </Group>
    );
  } else if (charges.length === 0) {
    body = (
      <Text c="dimmed" py="md">
        You do not have any charges yet.
      </Text>
    );
  } else {
    body = (
      <ScrollArea scrollbarSize={6} type="auto" styles={scrollAreaStyles}>
        <Stack gap="md">
          <Table miw={1100} bg="white">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Invoice Number</Table.Th>
                <Table.Th>Purpose</Table.Th>
                <Table.Th>Insurance Contribution</Table.Th>
                <Table.Th>Est. Patient Contribution</Table.Th>
                <Table.Th>Actual Patient Contribution</Table.Th>
                <Table.Th>Balance Due</Table.Th>
                <Table.Th>Due</Table.Th>
                <Table.Th>Payment Status</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {paginated.map((row) => (
                <ChargeRow key={row.id} row={row} onViewReceipt={setSelectedCharge} />
              ))}
            </Table.Tbody>
          </Table>
          {totalPages > 1 && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {charges.length} {charges.length === 1 ? 'charge' : 'charges'}
              </Text>
              <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
            </Group>
          )}
        </Stack>
      </ScrollArea>
    );
  }

  return (
    <>
      <Paper shadow="sm" radius="md" p="lg" className={sectionClasses.decorated}>
        <Title order={3} mb="md">
          History of Charges
        </Title>
        {body}
      </Paper>
      <ViewReceiptDrawer charge={selectedCharge} onClose={() => setSelectedCharge(null)} />
    </>
  );
}

function ChargeRow({
  row,
  onViewReceipt,
}: {
  row: PatientCharge;
  onViewReceipt: (row: PatientCharge) => void;
}): JSX.Element {
  return (
    <Table.Tr>
      <Table.Td>{row.invoiceNumber}</Table.Td>
      <Table.Td>{row.purpose}</Table.Td>
      <Table.Td>{formatCurrencyOrDash(row.insuranceContribution)}</Table.Td>
      <Table.Td>{formatCurrencyOrDash(row.estimatedPatientContribution)}</Table.Td>
      <Table.Td>{formatCurrencyOrDash(row.actualPatientContribution)}</Table.Td>
      <Table.Td>{row.balanceDue > 0 ? formatUsd(row.balanceDue) : 'N/A'}</Table.Td>
      <Table.Td>{row.dueDateISO ? dayjs(row.dueDateISO).format('MM/DD/YYYY') : '—'}</Table.Td>
      <Table.Td>
        <PaymentStatusBadge status={row.status} />
      </Table.Td>
      <Table.Td>
        <PaymentAction row={row} onViewReceipt={onViewReceipt} />
      </Table.Td>
    </Table.Tr>
  );
}

function PaymentStatusBadge({ status }: { status: PatientCharge['status'] }): JSX.Element {
  if (status === 'paid') {
    return <Badge color="green">Paid</Badge>;
  }
  if (status === 'overdue') {
    return (
      <Badge color="red" leftSection={<IconAlertTriangle size={12} />}>
        Overdue
      </Badge>
    );
  }
  if (status === 'cancelled') {
    return <Badge color="gray">Cancelled</Badge>;
  }
  return <Badge color="yellow">Pending</Badge>;
}

function getAppointmentId(row: PatientCharge): string | undefined {
  return row.charge.identifier?.find((id) => id.system === APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM)?.value;
}

function getPaymentPath(row: PatientCharge): string {
  const appointmentId = getAppointmentId(row);
  return row.selfPay || !appointmentId ? `/pay-charge/${row.id}` : `/pay/${appointmentId}`;
}

function PaymentAction({
  row,
  onViewReceipt,
}: {
  row: PatientCharge;
  onViewReceipt: (row: PatientCharge) => void;
}): JSX.Element {
  if (row.status === 'cancelled') {
    return <></>;
  }
  if (row.status === 'paid') {
    return (
      <TableAnchor size="sm" component="button" type="button" onClick={() => onViewReceipt(row)}>
        View Receipt
      </TableAnchor>
    );
  }
  if (row.id && row.balanceDue > 0) {
    return (
      <Link to={getPaymentPath(row)} style={{ color: BRAND, fontWeight: 600 }}>
        Pay Now →
      </Link>
    );
  }
  return <></>;
}

function ViewReceiptDrawer({ charge, onClose }: { charge: PatientCharge | null; onClose: () => void }): JSX.Element {
  return (
    <Drawer
      opened={charge !== null}
      onClose={onClose}
      title={charge ? `Receipt — ${charge.invoiceNumber}` : 'Receipt'}
      position="right"
      size="md"
    >
      {charge && (
        <Stack gap="sm">
          <Group justify="space-between">
            <Text fw={500}>Invoice Number</Text>
            <Text>{charge.invoiceNumber}</Text>
          </Group>
          <Group justify="space-between">
            <Text fw={500}>Date Paid</Text>
            <Text>
              {charge.charge.occurrenceDateTime ? dayjs(charge.charge.occurrenceDateTime).format('MM/DD/YYYY') : '—'}
            </Text>
          </Group>
          <Group justify="space-between">
            <Text fw={500}>Purpose</Text>
            <Text>{charge.purpose}</Text>
          </Group>
          <Group justify="space-between">
            <Text fw={500}>Insurance Contribution</Text>
            <Text>{formatCurrencyOrDash(charge.insuranceContribution)}</Text>
          </Group>
          <Group justify="space-between">
            <Text fw={500}>Amount Paid</Text>
            <Text>{formatCurrencyOrDash(charge.actualPatientContribution)}</Text>
          </Group>
        </Stack>
      )}
    </Drawer>
  );
}

function formatCurrencyOrDash(value: number | undefined): string {
  if (value === undefined) {
    return '—';
  }
  return formatUsd(value);
}
